// planningStore.js (updated for coherence)
import { defineStore } from "pinia";
import { ref } from "vue";
import dayjs from "dayjs";
import PlanningApi from "@/api/PlanningApi";

export const usePlanningStore = defineStore("planning", () => {
  const authVars = ref([]);
  const plannings = ref([]);
  const meetings = ref([]);
  const ofList = ref([]);
  const currentPlanning = ref(null);
  const loading = ref(false);
  const error = ref(null);

  // Préchargement des données (une seule fois au chargement de l'application)
  const preloadDone = ref(false);

  // Authentification
  const isAuthenticated = ref(false);
  const currentUser = ref(null);
  const authToken = ref(null);
  const candidatContactObjId = ref(null);

  // Récupérer la liste des plannings
  const fetchPlannings = (
    ofObjId,
    userAccountObjId,
    startDate = null,
    endDate = null,
    interviewManagerObjId = null
  ) => {
    loading.value = true;
    if (null === startDate || null === endDate) {
      const currentStartMonth = ref(dayjs().startOf("month"));
      startDate = currentStartMonth.value.startOf("month").format("YYYY-MM-DD");
      endDate = currentStartMonth.value
        .add(2, "month")
        .endOf("month")
        .format("YYYY-MM-DD");
    }
    return PlanningApi.getPlannings(
      ofObjId,
      userAccountObjId,
      startDate,
      endDate,
      interviewManagerObjId
    )
      .then((response) => {
        const { planningList, ...rest } = response.data;
        plannings.value = planningList;
        rest.ofObjId = rest.ofObjId || ofObjId;
        rest.userAccountObjId = rest.userAccountObjId || userAccountObjId;
        authVars.value = rest;

      })
      .catch((err) => {
        error.value =
          err.response?.data?.msg ||
          "Erreur lors de la récupération des plannings";
      })
      .finally(() => {
        loading.value = false;
      });
  };

  // Créer un nouveau planning
  const createPlanning = (ofObjId, userAccountObjId, planningData) => {
    if (planningData.interviewManagerObjId === null) {
      planningData.interviewManagerObjId = Math.floor(Date.now() / 1000) * -1;
    }

    loading.value = true;
    return PlanningApi.createPlanning(ofObjId, userAccountObjId, planningData)
      .then((response) => {
        fetchPlannings(ofObjId, userAccountObjId);
        return response.data;
      })
      .catch((err) => {
        error.value =
          err.response?.data?.msg || "Erreur lors de la création du planning";
        return Promise.reject(error.value);
      })
      .finally(() => {
        loading.value = false;
      });
  };

  // Supprimer un planning
  const deletePlanning = async (ofObjId, userAccountObjId, planningId) => {
    loading.value = true;
    return PlanningApi.deletePlanning(ofObjId, userAccountObjId, planningId)
      .then((response) => {
        console.log("deletePlanning response", response);
        fetchPlannings(ofObjId, userAccountObjId);
        return response.data;
      })
      .catch((err) => {
        error.value =
          err.response?.data?.msg ||
          err.response?.data?.detail ||
          "Erreur lors de la suppression du planning";
        return Promise.reject(error.value);
      })
      .finally(() => {
        loading.value = false;
      });
  };

  // Mettre à jour un planning
  const updatePlanning = (
    ofObjId,
    userAccountObjId,
    planningId,
    planningData
  ) => {
    if (planningData.interviewManagerObjId === null) {
      planningData.interviewManagerObjId = Math.floor(Date.now() / 1000) * -1;
    }

    loading.value = true;
    return PlanningApi.updatePlanning(
      ofObjId,
      userAccountObjId,
      planningId,
      planningData
    )
      .then((response) => {
        fetchPlannings(ofObjId, userAccountObjId);
        return response.data;
      })
      .catch((err) => {
        error.value =
          err.response?.data?.msg ||
          "Erreur lors de la mise à jour du planning";
        return Promise.reject(error.value);
      })
      .finally(() => {
        loading.value = false;
      });
  };

  // Récupérer les meetings d'un planning
  const fetchMeetings = (ofObjId, userAccountObjId, planningId) => {
    loading.value = true;
    return PlanningApi.getMeetings(ofObjId, userAccountObjId, planningId)
      .then((response) => {
        const { planningList, ...rest } = response.data;
        planningList.value = planningList;
        rest.ofObjId = rest.ofObjId || ofObjId;
        rest.userAccountObjId = rest.userAccountObjId || userAccountObjId;
        authVars.value = rest;
        return response.data;
      })
      .catch((err) => {
        error.value =
          err.response?.data?.msg ||
          "Erreur lors de la récupération des créneaux";
        return Promise.reject(error.value);
      })
      .finally(() => {
        loading.value = false;
      });
  };

  // Mettre à jour un meeting
  const updateMeeting = (ofObjId, userAccountObjId, meetingId, meetingData) => {
    loading.value = true;
    return PlanningApi.updateMeeting(
      ofObjId,
      userAccountObjId,
      meetingId,
      meetingData
    )
      .then((response) => {
        return response.data;
      })
      .catch((err) => {
        error.value =
          err.response?.data?.msg ||
          err.response?.data?.detail ||
          "Erreur lors de la mise à jour du créneau";
        return Promise.reject(error.value);
      })
      .finally(() => {
        loading.value = false;
      });
  };

  const createMeetingForm = (ofObjId, userAccountObjId, meetingId) => {
    loading.value = true;
    return PlanningApi.createMeetingForm(ofObjId, userAccountObjId, meetingId)
      .then((response) => {
        return response.data;
      })
      .catch((err) => {
        error.value =
          err.response?.data?.msg ||
          err.response?.data?.detail ||
          "Erreur lors de la création de la grille d'entretien";
        return Promise.reject(error.value);
      })
      .finally(() => {
        loading.value = false;
      });
  };

  const updateMeetingForm = (
    ofObjId,
    userAccountObjId,
    meetingId,
    formData
  ) => {
    return PlanningApi.updateMeetingForm(
      ofObjId,
      userAccountObjId,
      meetingId,
      formData
    )
      .then((response) => {
        return response.data;
      })
      .catch((err) => {
        error.value =
          err.response?.data?.msg ||
          err.response?.data?.detail ||
          "Erreur lors de la mise a jour de la grille d'entretien";
        return Promise.reject(error.value);
      });
  };

  const exportMeetingFormToEva = (
    ofObjId,
    userAccountObjId,
    meetingId,
    file = null
  ) => {
    return PlanningApi.exportMeetingFormToEva(
      ofObjId,
      userAccountObjId,
      meetingId,
      file
    )
      .then((response) => {
        return response.data;
      })
      .catch((err) => {
        error.value =
          err.response?.data?.msg ||
          err.response?.data?.detail ||
          "Erreur lors de l'export vers EVA";
        return Promise.reject(error.value);
      });
  };

  const getMeetingFormPreviewPdf = (ofObjId, userAccountObjId, meetingId) => {
    return PlanningApi.getMeetingFormPreviewPdf(
      ofObjId,
      userAccountObjId,
      meetingId
    ).catch((err) => {
      error.value =
        err.response?.data?.msg ||
        err.response?.data?.detail ||
        "Erreur lors du téléchargement de la grille d'entretien";
      return Promise.reject(error.value);
    });
  };

  // Récupérer les détails d'un candidat
  const fetchCandidacyDetails = (ofObjId, userAccountObjId, meetingId) => {
    loading.value = true;
    return PlanningApi.getCandidacyDetails(ofObjId, userAccountObjId, meetingId)
      .then((response) => {
        return response.data;
      })
      .catch((err) => {
        error.value =
          err.response?.data?.msg ||
          "Erreur lors de la récupération des détails du candidat";
        return Promise.reject(error.value);
      })
      .finally(() => {
        loading.value = false;
      });
  };

  // Récupérer les détails d'un meeting
  const fetchMeetingDetails = (ofObjId, userAccountObjId, meetingId) => {
    loading.value = true;
    return PlanningApi.getMeetingDetails(ofObjId, userAccountObjId, meetingId)
      .then((response) => {
        return response.data;
      })
      .catch((err) => {
        error.value =
          err.response?.data?.msg ||
          "Erreur lors de la récupération des détails du candidat";
        return Promise.reject(error.value);
      })
      .finally(() => {
        loading.value = false;
      });
  };

  // Recherche de candidatures
  const searchCandidacies = (ofObjId, userAccountObjId, searchTerm) => {
    return PlanningApi.searchCandidacies(ofObjId, userAccountObjId, searchTerm)
      .then((response) => {
        return response.data;
      })
      .catch((err) => {
        error.value =
          err.response?.data?.msg ||
          err.response?.data?.detail ||
          "Erreur lors de la recherche de candidatures";
        return Promise.reject(error.value);
      });
  };

  // Recherche de responsables d'entretien
  const searchInterviewManagers = (ofObjId, userAccountObjId, searchTerm) => {
    return PlanningApi.searchInterviewManagers(
      ofObjId,
      userAccountObjId,
      searchTerm
    )
      .then((response) => {
        return response.data;
      })
      .catch((err) => {
        error.value =
          err.response?.data?.msg ||
          err.response?.data?.detail ||
          "Erreur lors de la recherche de responsables";
        return Promise.reject(error.value);
      });
  };

  function setCandidatContactObjId(id) {
    candidatContactObjId.value = id;
    localStorage.setItem("planning_candidat_contact_obj_id", id);
  }

  function getCandidatContactObjId() {
    if (!candidatContactObjId.value) {
      candidatContactObjId.value = localStorage.getItem(
        "planning_candidat_contact_obj_id"
      );
    }
    return candidatContactObjId.value;
  }

  // === AUTHENTIFICATION ===

  // Fonction utilitaire pour définir l'utilisateur courant de manière cohérente
  const setCurrentUser = (userData) => {
    currentUser.value = {
      userAccountObjId: userData.userAccountObjId,
      login: userData.login,
      email: userData.email,
      firstname: userData.firstname,
      lastname: userData.lastname,
      expiresAt: userData.expiresAt,
    };
    localStorage.setItem(
      "planning_user_data",
      JSON.stringify(currentUser.value)
    );
  };

  // Connexion utilisateur
  const login = (email, password) => {
    loading.value = true;
    error.value = null;
    return PlanningApi.login(email, password)
      .then((response) => {
        if (response.data.success) {
          const userData = response.data.data;

          setCurrentUser(userData);
          authToken.value = userData.token;
          isAuthenticated.value = true;

          localStorage.setItem("planning_auth_token", userData.token);

          return response.data;
        } else {
          error.value = response.data.message || "Erreur de connexion";
          return Promise.reject(error.value);
        }
      })
      .catch((err) => {
        error.value =
          err.response?.data?.message ||
          err.message ||
          "Erreur lors de la connexion";
        isAuthenticated.value = false;
        currentUser.value = null;
        authToken.value = null;
        return Promise.reject(error.value);
      })
      .finally(() => {
        loading.value = false;
      });
  };

  const getAzureAuthUrl = (returnTo) => {
    return PlanningApi.getAzureAuthUrl(returnTo)
      .then((response) => {
        return response.data.authUrl;
      })
      .catch((err) => {
        error.value =
          err.response?.data?.message ||
          "Erreur lors de la récupération de l'URL Azure AD";
        return Promise.reject(error.value);
      });
  };

  // Gérer le callback Azure
  const handleAzureCallback = (token, userAccountObjId) => {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const isExpired = payload.exp && payload.exp * 1000 < Date.now();

      if (isExpired) {
        error.value = "Token expiré";
        return Promise.reject(error.value);
      }

      const userData = {
        userAccountObjId: parseInt(userAccountObjId, 10) || userAccountObjId,
        login: payload.email || "",
        email: payload.email || "",
        firstname: "",
        lastname: "",
      };

      setCurrentUser(userData);
      authToken.value = token;
      isAuthenticated.value = true;

      localStorage.setItem("planning_auth_token", token);

      return Promise.resolve({ success: true, user: currentUser.value });
    } catch (err) {
      error.value = "Token JWT invalide";
      return Promise.reject(error.value);
    }
  };

  // Déconnexion
  const logout = () => {
    isAuthenticated.value = false;
    currentUser.value = null;
    authToken.value = null;

    localStorage.removeItem("planning_auth_token");
    localStorage.removeItem("planning_user_data");

    plannings.value = [];
    meetings.value = [];
    authVars.value = [];
  };

  // Vérification locale du token JWT (pas d'appel backend)
  const verifyToken = () => {
    const token = localStorage.getItem("planning_auth_token");
    const storedUserData = localStorage.getItem("planning_user_data");

    if (!token || !storedUserData) {
      return false;
    }

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const isExpired = payload.exp && payload.exp * 1000 < Date.now();

      if (isExpired) {
        logout();
        return false;
      }

      const user = JSON.parse(storedUserData);
      setCurrentUser(user);
      authToken.value = token;
      isAuthenticated.value = true;

      return true;
    } catch (e) {
      console.warn("Token JWT invalide:", e);
      logout();
      return false;
    }
  };

  // Initialisation de l'authentification
  const initAuth = () => {
    return verifyToken();
  };

  // Réinitialiser les erreurs
  const clearError = () => {
    error.value = null;
  };

  const fetchConvocationPreview = (ofObjId, userAccountObjId, meetingId) => {
    return PlanningApi.getConvocationPreview(
      ofObjId,
      userAccountObjId,
      meetingId
    )
      .then((response) => {
        return response.data;
      })
      .catch((err) => {
        error.value =
          err.response?.data?.msg ||
          "Erreur lors du chargement de l’aperçu de convocation";
        return Promise.reject(error.value);
      });
  };

  const fetchOfList = (ofObjId, userAccountObjId) => {
    loading.value = true;
    return PlanningApi.getOfList(ofObjId, userAccountObjId)
      .then((response) => {
        ofList.value = response.data;
        return response.data;
      })
      .catch((err) => {
        error.value =
          err.response?.data?.msg ||
          "Erreur lors de la récupération de la liste des centres";
        return Promise.reject(error.value);
      })
      .finally(() => {
        loading.value = false;
      });
  };

  return {
    authVars,
    plannings,
    meetings,
    ofList,
    currentPlanning,
    loading,
    error,
    preloadDone,
    isAuthenticated,
    currentUser,
    authToken,
    fetchPlannings,
    createPlanning,
    deletePlanning,
    updatePlanning,
    searchCandidacies,
    searchInterviewManagers,
    fetchMeetings,
    updateMeeting,
    createMeetingForm,
    updateMeetingForm,
    exportMeetingFormToEva,
    getMeetingFormPreviewPdf,
    fetchCandidacyDetails,
    fetchMeetingDetails,
    getAzureAuthUrl,
    handleAzureCallback,
    login,
    logout,
    initAuth,
    clearError,
    fetchConvocationPreview,
    fetchOfList,
    getCandidatContactObjId,
    setCandidatContactObjId,
  };
});
